import type { ModelAsset } from '@/content/schema';
import { useGLTF } from '@react-three/drei';
import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { buildBatches, presetKey } from '@/render/batch';
import { batchPointer, hoverElement } from '@/render/pick';
import { rigLayer, type LayerRig } from '@/render/rig';
import { useSelectionStore } from '@/state/selection';
import { MODELS_BASE } from './preload';
import { glbSource } from './sources';

export interface LayerHandle {
  model: ModelAsset;
  rig: LayerRig;
  box: THREE.Box3;
}

export function GlbLayer({
  model,
  onReady,
}: {
  model: ModelAsset;
  onReady: (id: string, handle: LayerHandle | null) => void;
}) {
  const url = MODELS_BASE + model.glb;
  const { scene } = useGLTF(url, true, true);
  const container = useMemo(() => new THREE.Group(), []);

  const { tree, box } = useMemo(() => {
    const { object } = glbSource(scene);
    return { tree: object, box: new THREE.Box3().setFromObject(object) };
  }, [scene]);

  useEffect(() => {
    const role =
      model.discipline === 'space'
        ? 'space'
        : model.discipline === 'arch' || model.discipline === 'structure'
          ? 'shell'
          : 'revealed';
    const batches = buildBatches(tree, { keyOf: presetKey });
    const rig = rigLayer(batches, role, model.discipline === 'mep');
    container.add(batches.root);
    onReady(model.id, { model, rig, box });
    return () => {
      onReady(model.id, null);
      hoverElement(null);
      rig.dispose();
    };
  }, [tree, box, container, model, onReady]);

  const handlers = useMemo(
    () =>
      batchPointer(model.id, () => {
        const pick = useSelectionStore.getState().pick;
        if (pick === 'none') return false;
        if (pick === 'all') return true;
        return pick === 'space' ? model.discipline === 'space' : model.discipline !== 'space';
      }),
    [model],
  );

  return <primitive object={container} {...handlers} />;
}
